import { useMemo } from "react";
import { Button } from "../../../../shadcn/components/ui/button";
import { DateTime } from "luxon";
import { useRouter } from "next/router";

export type tableData = {
  [x: string]: any;
  id: string;
};

interface props {
  status: string[];
  eventData: any[];
  sortBy: string;
  borrowerSearch: string;
  auctionDate: Date | undefined;
}

export default function NotificationsDetailsWrapper({
  status,
  eventData,
  sortBy,
  borrowerSearch,
  auctionDate,
}: props) {
  const router = useRouter();

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...(eventData || [])];

    // Filter by borrower name
    if (borrowerSearch) {
      filtered = filtered.filter((item) =>
        item.borrower_name?.toLowerCase().includes(borrowerSearch.toLowerCase())
      );
    }

    // Filter by auction date
    if (auctionDate) {
      filtered = filtered.filter((item) => {
        if (!item.auction_start_date) return false;
        try {
          let itemDate = DateTime.fromISO(item.auction_start_date);
          if (!itemDate.isValid) {
            itemDate = DateTime.fromFormat(
              item.auction_start_date,
              "dd MMMM yyyy hh:mm a",
            );
          }
          if (!itemDate.isValid) return false;
          const filterDate = DateTime.fromJSDate(auctionDate).startOf("day");
          return itemDate.startOf("day").equals(filterDate);
        } catch {
          return false;
        }
      });
    }

    const safeMillis = (val: any) => {
      if (!val) return 0;
      // Try ISO first (e.g. "2025-12-03T09:07:18.134Z")
      let dt = DateTime.fromISO(val);
      if (dt.isValid) return dt.toMillis();
      // Try pre-formatted string (e.g. "3 December 2025 at 12:51 pm")
      dt = DateTime.fromFormat(val, "d MMMM yyyy 'at' h:mm a");
      if (dt.isValid) return dt.toMillis();
      // Try auction date format (e.g. "04 December 2025 01:00 PM")
      dt = DateTime.fromFormat(val, "dd MMMM yyyy hh:mm a");
      if (dt.isValid) return dt.toMillis();
      return 0;
    };

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "submitted_date_latest":
          return (
            safeMillis(b.submission_time || b.updated_at) -
            safeMillis(a.submission_time || a.updated_at)
          );
        case "submitted_date_oldest":
          return (
            safeMillis(a.submission_time || a.updated_at) -
            safeMillis(b.submission_time || b.updated_at)
          );
        case "auction_date_latest":
          return (
            safeMillis(b.auction_start_date) - safeMillis(a.auction_start_date)
          );
        case "auction_date_oldest":
          return (
            safeMillis(a.auction_start_date) - safeMillis(b.auction_start_date)
          );
        default:
          return 0;
      }
    });

    return filtered;
  }, [eventData, sortBy, borrowerSearch, auctionDate]);

  if (!eventData) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const getStatusText = () => {
    if (status[0] === "accepted") return "Completed";
    return (
      status[0]?.charAt(0).toUpperCase() + status[0]?.slice(1) || "Pending"
    );
  };

  const getStatusClass = () => {
    if (status[0] === "pending") return "bg-[#fff3e5] text-[#b14b00]";
    if (status[0] === "accepted") return "bg-[#e6f7ea] text-[#1a7f3a]";
    return "bg-[#fff1f1] text-[#a12b2b]";
  };
  const maskName = (name: any) => {
    if (!name || name.length === 0) return "";
    if (name.length <= 2) return name.charAt(0) + "***";
    return name.charAt(0) + "**********" + name.charAt(name.length - 1);
  };

  return (
    <div className="w-full">
      {/* Card Container */}
      <div className="bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.08)] p-[18px]">
        {/* Request List */}
        <div>
          {filteredAndSortedData.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500 text-sm">No requests found</p>
            </div>
          ) : (
            <div>
              {filteredAndSortedData.map((item, index) => {
                console.log("item:---->>> ", item);
                const statusText = getStatusText();

                return (
                  <div
                    key={item.uuid || index}
                    className="flex justify-between items-start py-3.5 px-2.5 border-b border-[#f0f0f0] hover:bg-[#fff8f0] cursor-pointer transition-colors"
                  >
                    {/* Left Section */}
                    <div className="flex-1">
                      <div className="font-bold mb-0.5 text-gray-800 uppercase">
                        {maskName(item.bidder_name) || "N/A"}
                      </div>
                      <div className="text-xs text-[#666] mt-0.5">
                        Borrower: {item.borrower_name || "N/A"}
                      </div>
                      <div className="text-xs text-[#666] mt-0.5">
                        Auction: {item.auction_start_date || "N/A"}
                      </div>
                      <div className="text-xs text-[#666] mt-0.5">
                        Submitted:{" "}
                        {item.submission_time || item.updated_at || "N/A"}
                      </div>
                    </div>

                    {/* Right Section */}
                    <div className="text-right flex flex-col items-end gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded-full inline-block font-medium ${getStatusClass()}`}
                      >
                        {item.event_id}
                      </span>

                      <Button
                        className="bg-[#f5761a] hover:bg-[#f5761a]/92 text-white rounded-lg px-3.5 py-2 text-xs font-semibold h-auto"
                        onClick={() => {
                          router.push(`/dashboard/bid-requests/${item.uuid}`);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="mt-3 text-xs text-[#888]">
            Total Requests - {filteredAndSortedData.length}
          </div>
        </div>
      </div>
    </div>
  );
}
